import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Check, Clock, Edit3, MessageSquare, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  useApproveComment,
  useCreateComment,
  useDeleteComment,
  useGetCommentsByInvestigation,
  useUpdateComment,
} from '@/application/services/comment.service'
import { useAuthStore } from '@/application/stores/auth.store'
import type { Comment } from '@/domains/types'
import { Badge } from '@/ui/components/common/Badge'
import { Button } from '@/ui/components/common/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/components/common/Dialog'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { Textarea } from '@/ui/components/common/Textarea'

const makeCommentSchema = (translate: (key: string, opts?: Record<string, unknown>) => string) =>
  z.object({
    content: z
      .string()
      .min(1, translate('validation.comment_empty'))
      .max(2000, translate('validation.comment_max')),
  })
type CommentFormData = z.infer<ReturnType<typeof makeCommentSchema>>

function isOwnComment(comment: Comment, user: { id: string; username: string } | null): boolean {
  if (!user) return false
  // Fallback on username for comments created before the userId migration.
  return comment.userId === user.id || (!comment.userId && comment.username === user.username)
}

/**
 * Comments thread for the public investigation (article) read view. Renders
 * the inline "post a comment" form for authenticated visitors (with a login
 * prompt otherwise), the list of approved top-level comments each with its
 * nested replies (one level deep), a reply form per comment, per-row
 * edit/delete controls (in a dialog) for the comment's own author, and
 * Valider/Rejeter moderation controls on anyone else's pending comment (the
 * API only ever includes another user's pending comment for a moderator —
 * investigation author/team member or streamer/admin/moderator — so no
 * separate permission check is needed here). Owns: comments fetch,
 * create/update/delete/approve mutations, the new-comment and reply form
 * schemas, and the edit-comment dialog state.
 *
 * Mounted with `id="comments"` so the anchor link next to the article byline
 * (`ReadArticleHeader`) can jump straight to it.
 */
export function ArticleCommentsSection({ investigationId }: { investigationId: string }) {
  const { t } = useTranslation()
  const commentSchema = useMemo(() => makeCommentSchema(t), [t])
  const { isAuthenticated, user } = useAuthStore()

  const [replyToId, setReplyToId] = useState<string | null>(null)
  const [editCommentId, setEditCommentId] = useState<string | null>(null)
  const [editCommentContent, setEditCommentContent] = useState('')

  const { data: comments, isLoading: isLoadingComments } =
    useGetCommentsByInvestigation(investigationId)
  const { mutateAsync: createComment } = useCreateComment(investigationId)
  const { mutateAsync: updateComment } = useUpdateComment(investigationId)
  const { mutateAsync: deleteComment } = useDeleteComment(investigationId)
  const { mutateAsync: approveComment } = useApproveComment(investigationId)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CommentFormData>({ resolver: zodResolver(commentSchema) })

  const {
    register: registerReply,
    handleSubmit: handleReplySubmit,
    reset: resetReply,
    formState: { errors: replyErrors, isSubmitting: isSubmittingReply },
  } = useForm<CommentFormData>({ resolver: zodResolver(commentSchema) })

  const topLevelComments = (comments ?? []).filter((comment) => !comment.parentId)
  const repliesByParentId = (comments ?? []).reduce<Record<string, Comment[]>>(
    (grouped, comment) => {
      if (!comment.parentId) return grouped
      grouped[comment.parentId] = [...(grouped[comment.parentId] ?? []), comment]
      return grouped
    },
    {},
  )

  const onSubmit = async (data: CommentFormData) => {
    const newComment = await createComment({ content: data.content })
    reset()
    if (newComment.status === 'approved') {
      toast.success(t('article_comments.published_toast'))
    } else {
      toast.info(t('article_comments.my_pending_note'))
    }
  }

  const onSubmitReply = async (parentId: string, data: CommentFormData) => {
    const newReply = await createComment({ content: data.content, parentId })
    resetReply()
    setReplyToId(null)
    if (newReply.status === 'approved') {
      toast.success(t('article_comments.reply_published_toast'))
    } else {
      toast.info(t('article_comments.my_pending_note'))
    }
  }

  const handleDeleteOwnComment = async (commentId: string) => {
    if (window.confirm(t('article_comments.delete_confirm'))) {
      try {
        await deleteComment(commentId)
        toast.success(t('article_comments.deleted_toast'))
      } catch (error: unknown) {
        toast.error(error instanceof Error ? error.message : String(error))
      }
    }
  }

  const handleValidateComment = async (commentId: string) => {
    try {
      await approveComment(commentId)
      toast.success(t('article_comments.approved_toast'))
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : String(error))
    }
  }

  const handleRejectComment = async (commentId: string) => {
    if (window.confirm(t('article_comments.delete_confirm'))) {
      try {
        await deleteComment(commentId)
        toast.success(t('article_comments.deleted_toast'))
      } catch (error: unknown) {
        toast.error(error instanceof Error ? error.message : String(error))
      }
    }
  }

  const handleUpdateOwnComment = async () => {
    if (!editCommentId) return
    try {
      await updateComment({ commentId: editCommentId, content: editCommentContent })
      toast.success(t('article_comments.updated_toast'))
      setEditCommentId(null)
      setEditCommentContent('')
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : String(error))
    }
  }

  const renderCommentRow = (comment: Comment, { isReply = false }: { isReply?: boolean } = {}) => (
    <div
      key={comment.commentId}
      className={`bg-background border border-border rounded-lg p-4 ${isReply ? 'ml-6 sm:ml-10' : ''}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
            {comment.username.charAt(0).toUpperCase()}
          </div>
          <span className="text-sm font-medium">{comment.username}</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {new Date(comment.createdAt).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </span>
      </div>
      <p className="text-sm text-foreground/80 leading-relaxed">{comment.content}</p>

      <div className="mt-2 pt-2 border-t border-border/40 flex items-center justify-between">
        <div>
          {isOwnComment(comment, user) && comment.status === 'pending' && (
            <span className="text-xs text-amber-600 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {t('article_comments.my_pending_note')}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!isReply && isAuthenticated && comment.status === 'approved' && (
            <button
              onClick={() =>
                setReplyToId(replyToId === comment.commentId ? null : comment.commentId)
              }
              className="text-xs text-primary hover:underline mr-2"
            >
              {t('article_comments.reply')}
            </button>
          )}
          {isOwnComment(comment, user) ? (
            <>
              <button
                onClick={() => {
                  setEditCommentId(comment.commentId)
                  setEditCommentContent(comment.content)
                }}
                className="p-1 rounded hover:bg-muted/50 text-muted-foreground transition-colors"
                title={t('article_comments.edit')}
              >
                <Edit3 className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleDeleteOwnComment(comment.commentId)}
                className="p-1 rounded hover:bg-destructive/10 text-destructive transition-colors"
                title={t('article_comments.delete')}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </>
          ) : (
            // A `pending` comment from someone else is only ever visible to a
            // moderator (author/team member or streamer/admin/moderator) —
            // the API only includes others' pending comments for them.
            comment.status === 'pending' && (
              <>
                <button
                  onClick={() => handleValidateComment(comment.commentId)}
                  className="p-1 rounded hover:bg-success/10 text-success transition-colors"
                  title={t('article_comments.moderate_validate')}
                >
                  <Check className="w-3 h-3" />
                </button>
                <button
                  onClick={() => handleRejectComment(comment.commentId)}
                  className="p-1 rounded hover:bg-destructive/10 text-destructive transition-colors"
                  title={t('article_comments.moderate_reject')}
                >
                  <X className="w-3 h-3" />
                </button>
              </>
            )
          )}
        </div>
      </div>

      {!isReply && replyToId === comment.commentId && (
        <form
          onSubmit={handleReplySubmit((data) => onSubmitReply(comment.commentId, data))}
          className="mt-3 ml-6 sm:ml-10"
        >
          <Textarea
            {...registerReply('content')}
            placeholder={t('article_comments.reply_placeholder')}
            className="mb-2 resize-y bg-card"
            rows={2}
          />
          {replyErrors.content && (
            <p className="text-destructive text-xs mb-2">{replyErrors.content.message}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setReplyToId(null)
                resetReply()
              }}
            >
              {t('article_comments.cancel_reply')}
            </Button>
            <Button type="submit" size="sm" isLoading={isSubmittingReply}>
              {t('article_comments.reply_submit')}
            </Button>
          </div>
        </form>
      )}
    </div>
  )

  return (
    <section id="comments" className="mt-12 pt-8 border-t border-border scroll-mt-24">
      <h2 className="font-serif text-xl font-bold mb-6 flex items-center gap-2">
        <MessageSquare className="h-5 w-5 text-primary" />
        {t('article_comments.title')}
        {comments && (
          <Badge variant="secondary" className="ml-1">
            {comments.length}
          </Badge>
        )}
      </h2>

      {isAuthenticated ? (
        <form onSubmit={handleSubmit(onSubmit)} className="mb-8">
          <div className="bg-background border border-border rounded-lg p-4">
            <Textarea
              {...register('content')}
              placeholder={t('article_comments.placeholder')}
              className="mb-3 resize-y bg-card"
              rows={3}
            />
            {errors.content && (
              <p className="text-destructive text-xs mb-2">{errors.content.message}</p>
            )}
            <div className="flex justify-end">
              <Button type="submit" size="sm" isLoading={isSubmitting}>
                {t('article_comments.submit')}
              </Button>
            </div>
          </div>
        </form>
      ) : (
        <div className="mb-8 text-center py-6 bg-background border border-border rounded-lg text-sm text-muted-foreground">
          {t('article_comments.login_to_comment')}{' '}
          <Link to="/login" className="text-primary hover:underline">
            {t('auth.login_link')}
          </Link>
        </div>
      )}

      {isLoadingComments ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_item, index) => (
            <Skeleton key={index} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      ) : topLevelComments.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground text-sm">
          <MessageSquare className="h-8 w-8 mx-auto mb-3 opacity-30" />
          <p>{t('article_comments.no_comments')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {topLevelComments.map((comment) => (
            <div key={comment.commentId} className="space-y-3">
              {renderCommentRow(comment)}
              {(repliesByParentId[comment.commentId] ?? []).map((reply) =>
                renderCommentRow(reply, { isReply: true }),
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={!!editCommentId}
        onOpenChange={(open) => {
          if (!open) {
            setEditCommentId(null)
            setEditCommentContent('')
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">
              {t('article_comments.edit_dialog_title')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Textarea
              value={editCommentContent}
              onChange={(event) => setEditCommentContent(event.target.value)}
              rows={4}
              className="resize-y bg-card"
            />
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  {t('common.cancel')}
                </Button>
              </DialogClose>
              <Button type="button" onClick={handleUpdateOwnComment}>
                {t('article_comments.edit_submit')}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  )
}
